import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  ClipboardList,
  Plus,
  CheckCircle,
  AlertCircle,
  Home,
  Loader2,
  RefreshCw,
  FileSignature,
  Box,
  Calculator,
  Shield,
  Trash2,
  ChevronLeft,
  Save,
  AlertTriangle,
  Info,
  Zap,
  Refrigerator,
  Flame,
  WashingMachine,
  Sofa,
  Bed,
  Tv,
  Lightbulb,
  UtensilsCrossed
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONDITION_OPTIONS = [
  { value: 'excellent', label: 'Excellent', color: 'bg-green-100 text-green-700' },
  { value: 'good', label: 'Good', color: 'bg-blue-100 text-blue-700' },
  { value: 'fair', label: 'Fair', color: 'bg-amber-100 text-amber-700' },
  { value: 'poor', label: 'Poor', color: 'bg-orange-100 text-orange-700' }
];

const ROOM_OPTIONS = [
  'Living Room', 'Kitchen', 'Bedroom 1', 'Bedroom 2', 'Bedroom 3', 
  'Bathroom', 'En-Suite', 'Hallway', 'Garden', 'Garage', 'Utility Room'
];

// Quick-add preset items with pre-filled values
const QUICK_ADD_PRESETS = [
  {
    id: 'fridge_freezer',
    name: 'Fridge Freezer',
    icon: Refrigerator,
    room: 'Kitchen',
    category: 'white_goods',
    subcategory: 'Fridge Freezer',
    manufacturer: '',
    purchase_price: 450,
    condition: 'good',
    color: 'bg-blue-50 border-blue-200 hover:bg-blue-100'
  },
  {
    id: 'washing_machine',
    name: 'Washing Machine',
    icon: WashingMachine,
    room: 'Kitchen',
    category: 'white_goods',
    subcategory: 'Washing Machine',
    manufacturer: '',
    purchase_price: 350,
    condition: 'good',
    color: 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100'
  },
  {
    id: 'oven',
    name: 'Oven',
    icon: Flame,
    room: 'Kitchen',
    category: 'white_goods',
    subcategory: 'Oven',
    manufacturer: '',
    purchase_price: 400,
    condition: 'good',
    color: 'bg-orange-50 border-orange-200 hover:bg-orange-100'
  },
  {
    id: 'hob',
    name: 'Hob',
    icon: Flame,
    room: 'Kitchen',
    category: 'white_goods',
    subcategory: 'Hob',
    manufacturer: '',
    purchase_price: 250,
    condition: 'good',
    color: 'bg-red-50 border-red-200 hover:bg-red-100'
  },
  {
    id: 'dishwasher',
    name: 'Dishwasher',
    icon: Box,
    room: 'Kitchen',
    category: 'white_goods',
    subcategory: 'Dishwasher',
    manufacturer: '',
    purchase_price: 300,
    condition: 'good',
    color: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100'
  },
  {
    id: 'microwave',
    name: 'Microwave',
    icon: Box,
    room: 'Kitchen',
    category: 'white_goods',
    subcategory: 'Microwave',
    manufacturer: '',
    purchase_price: 100,
    condition: 'good',
    color: 'bg-gray-50 border-gray-200 hover:bg-gray-100'
  },
  {
    id: 'sofa',
    name: 'Sofa',
    icon: Sofa,
    room: 'Living Room',
    category: 'furniture',
    subcategory: 'Sofa',
    manufacturer: '',
    purchase_price: 600,
    condition: 'good',
    color: 'bg-amber-50 border-amber-200 hover:bg-amber-100'
  },
  {
    id: 'double_bed',
    name: 'Double Bed',
    icon: Bed,
    room: 'Bedroom 1',
    category: 'furniture',
    subcategory: 'Bed Frame',
    manufacturer: '',
    purchase_price: 400,
    condition: 'good',
    color: 'bg-purple-50 border-purple-200 hover:bg-purple-100'
  },
  {
    id: 'mattress',
    name: 'Mattress',
    icon: Bed,
    room: 'Bedroom 1',
    category: 'furniture',
    subcategory: 'Mattress',
    manufacturer: '',
    purchase_price: 350,
    condition: 'good',
    color: 'bg-violet-50 border-violet-200 hover:bg-violet-100'
  },
  {
    id: 'wardrobe',
    name: 'Wardrobe',
    icon: Box,
    room: 'Bedroom 1',
    category: 'furniture',
    subcategory: 'Wardrobe',
    manufacturer: '',
    purchase_price: 300,
    condition: 'good',
    color: 'bg-stone-50 border-stone-200 hover:bg-stone-100'
  },
  {
    id: 'dining_table',
    name: 'Dining Table',
    icon: UtensilsCrossed,
    room: 'Living Room',
    category: 'furniture',
    subcategory: 'Dining Table',
    manufacturer: '',
    purchase_price: 250,
    condition: 'good',
    color: 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100'
  },
  {
    id: 'television',
    name: 'Television',
    icon: Tv,
    room: 'Living Room',
    category: 'electronics',
    subcategory: 'Television',
    manufacturer: '',
    purchase_price: 400,
    condition: 'good',
    color: 'bg-slate-50 border-slate-200 hover:bg-slate-100'
  },
  {
    id: 'ceiling_light',
    name: 'Ceiling Light',
    icon: Lightbulb,
    room: 'Living Room',
    category: 'fixtures',
    subcategory: 'Ceiling Light',
    manufacturer: '',
    purchase_price: 80,
    condition: 'good',
    color: 'bg-lime-50 border-lime-200 hover:bg-lime-100'
  },
  {
    id: 'smoke_alarm',
    name: 'Smoke Alarm',
    icon: AlertCircle,
    room: 'Hallway',
    category: 'fixtures',
    subcategory: 'Smoke Alarm',
    manufacturer: '',
    purchase_price: 25,
    condition: 'excellent',
    color: 'bg-rose-50 border-rose-200 hover:bg-rose-100'
  }
];

export default function InventoryCreate() {
  const { propertyId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState({});
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(propertyId || '');
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState('items');
  
  // New item form
  const [newItem, setNewItem] = useState({
    room: '',
    item_name: '',
    description: '',
    condition: 'good',
    quantity: 1,
    category: '',
    subcategory: '',
    year_of_purchase: new Date().getFullYear(),
    manufacturer: '',
    purchase_price: '',
    white_goods_cover: false,
    notes: ''
  });
  
  // Declaration state
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  
  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [token]);
  
  // Fetch properties
  const fetchProperties = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/properties`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProperties(data.properties || data || []);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    fetchCategories();
    fetchProperties();
  }, [fetchCategories, fetchProperties]);
  
  // Calculate depreciation
  const calculateDepreciation = (purchasePrice, yearOfPurchase) => {
    if (!purchasePrice || !yearOfPurchase) return null;
    
    const currentYear = new Date().getFullYear();
    const ageYears = currentYear - yearOfPurchase;
    
    // 5-year straight-line depreciation (20% per year)
    const depreciationPercent = Math.min(ageYears * 20, 100);
    const depreciatedValue = purchasePrice * (1 - depreciationPercent / 100);
    
    return {
      purchasePrice,
      depreciatedValue: Math.max(0, depreciatedValue),
      ageYears,
      depreciationPercent,
      whiteGoodsCoverEligible: ageYears <= 3
    };
  };
  
  // Add item to list
  const handleAddItem = () => {
    if (!newItem.room || !newItem.item_name) {
      toast.error('Please fill in room and item name');
      return;
    }
    
    const depreciation = calculateDepreciation(
      parseFloat(newItem.purchase_price) || 0,
      parseInt(newItem.year_of_purchase)
    );
    
    const item = {
      ...newItem,
      id: `temp_${Date.now()}`,
      purchase_price: parseFloat(newItem.purchase_price) || null,
      year_of_purchase: parseInt(newItem.year_of_purchase) || null,
      depreciated_value: depreciation?.depreciatedValue || null,
      white_goods_cover_eligible: depreciation?.whiteGoodsCoverEligible || false,
      // Validate white goods cover
      white_goods_cover: newItem.category === 'white_goods' && 
                        depreciation?.whiteGoodsCoverEligible && 
                        newItem.white_goods_cover
    };
    
    setItems(prev => [...prev, item]);
    
    // Reset form but keep room for convenience
    setNewItem({
      room: newItem.room,
      item_name: '',
      description: '',
      condition: 'good',
      quantity: 1,
      category: '',
      subcategory: '',
      year_of_purchase: new Date().getFullYear(),
      manufacturer: '',
      purchase_price: '',
      white_goods_cover: false,
      notes: ''
    });
    
    toast.success('Item added to inventory');
  };
  
  // Remove item
  const handleRemoveItem = (itemId) => {
    setItems(prev => prev.filter(i => i.id !== itemId));
    toast.success('Item removed');
  };
  
  // Quick-add preset item
  const handleQuickAdd = (preset) => {
    const currentYear = new Date().getFullYear();
    const depreciation = calculateDepreciation(preset.purchase_price, currentYear);
    
    const item = {
      id: `temp_${Date.now()}`,
      room: preset.room,
      item_name: preset.name,
      description: '',
      condition: preset.condition,
      quantity: 1,
      category: preset.category,
      subcategory: preset.subcategory,
      year_of_purchase: currentYear,
      manufacturer: preset.manufacturer,
      purchase_price: preset.purchase_price,
      depreciated_value: depreciation?.depreciatedValue || preset.purchase_price,
      white_goods_cover_eligible: depreciation?.whiteGoodsCoverEligible || true,
      white_goods_cover: preset.category === 'white_goods', // Auto-enable for white goods
      notes: ''
    };
    
    setItems(prev => [...prev, item]);
    toast.success(`${preset.name} added to inventory`);
  };
  
  // Save inventory
  const handleSaveInventory = async () => {
    if (!selectedProperty) {
      toast.error('Please select a property');
      return;
    }
    
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/inventory/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          property_id: selectedProperty,
          inventory_type: 'full',
          items: items.map(({ id, depreciated_value, white_goods_cover_eligible, ...item }) => item),
          notes: ''
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Inventory created successfully!');
        navigate(`/inventory/${data.inventory_id}`);
      } else {
        toast.error(data.detail || 'Failed to create inventory');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setSaving(false);
    }
  };
  
  // Get subcategories for selected category
  const getSubcategories = () => {
    if (!newItem.category || !categories[newItem.category]) return [];
    return categories[newItem.category] || [];
  };
  
  // Calculate totals
  const calculateTotals = () => {
    let totalPurchaseValue = 0;
    let totalDepreciatedValue = 0;
    let whiteGoodsCount = 0;
    let whiteGoodsCoveredCount = 0;
    
    items.forEach(item => {
      if (item.purchase_price) {
        totalPurchaseValue += item.purchase_price * (item.quantity || 1);
      }
      if (item.depreciated_value) {
        totalDepreciatedValue += item.depreciated_value * (item.quantity || 1);
      }
      if (item.category === 'white_goods') {
        whiteGoodsCount++;
        if (item.white_goods_cover) {
          whiteGoodsCoveredCount++;
        }
      }
    });
    
    return {
      totalPurchaseValue,
      totalDepreciatedValue,
      whiteGoodsCount,
      whiteGoodsCoveredCount
    };
  };
  
  const totals = calculateTotals();
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="inventory-create-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Property Inventory</h1>
              <p className="text-gray-600">Add items with categories, values, and white goods cover</p>
            </div>
          </div>
          <Button 
            onClick={handleSaveInventory} 
            disabled={saving || items.length === 0 || !selectedProperty}
            data-testid="save-inventory-btn"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" />Save Inventory</>
            )}
          </Button>
        </div>
        
        {/* Property Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="w-5 h-5" />
              Select Property
            </CardTitle>
          </CardHeader>
          <CardContent>
            <select
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="w-full border rounded-lg p-3"
              data-testid="property-select"
            >
              <option value="">-- Select a property --</option>
              {properties.map(prop => (
                <option key={prop.id} value={prop.id}>
                  {prop.address_line_1}, {prop.city} {prop.postcode}
                </option>
              ))}
            </select>
          </CardContent>
        </Card>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="py-4 text-center">
              <Box className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold">{items.length}</p>
              <p className="text-sm text-gray-500">Total Items</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <Calculator className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold">£{totals.totalPurchaseValue.toLocaleString()}</p>
              <p className="text-sm text-gray-500">Purchase Value</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <AlertTriangle className="w-8 h-8 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold">£{totals.totalDepreciatedValue.toLocaleString()}</p>
              <p className="text-sm text-gray-500">Depreciated Value</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <Shield className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold">{totals.whiteGoodsCoveredCount}/{totals.whiteGoodsCount}</p>
              <p className="text-sm text-gray-500">White Goods Covered</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="items" data-testid="tab-items">
              <ClipboardList className="w-4 h-4 mr-2" />
              Items ({items.length})
            </TabsTrigger>
            <TabsTrigger value="quick" data-testid="tab-quick">
              <Zap className="w-4 h-4 mr-2" />
              Quick Add
            </TabsTrigger>
            <TabsTrigger value="add" data-testid="tab-add">
              <Plus className="w-4 h-4 mr-2" />
              Custom Item
            </TabsTrigger>
            <TabsTrigger value="declaration" data-testid="tab-declaration">
              <FileSignature className="w-4 h-4 mr-2" />
              Declaration
            </TabsTrigger>
          </TabsList>
          
          {/* Items List Tab */}
          <TabsContent value="items" className="mt-6">
            {items.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Box className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-600">No Items Yet</h3>
                  <p className="text-sm text-gray-500 mt-2 mb-4">
                    Start adding items to your inventory
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={() => setActiveTab('quick')} variant="default" data-testid="quick-add-btn">
                      <Zap className="w-4 h-4 mr-2" />
                      Quick Add
                    </Button>
                    <Button onClick={() => setActiveTab('add')} variant="outline" data-testid="add-first-item-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Custom Item
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Group items by room */}
                {[...new Set(items.map(i => i.room))].map(room => (
                  <Card key={room}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Home className="w-5 h-5" />
                        {room}
                      </CardTitle>
                      <CardDescription>
                        {items.filter(i => i.room === room).length} items
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {items.filter(i => i.room === room).map((item) => (
                          <div 
                            key={item.id}
                            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                            data-testid={`item-${item.id}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium">{item.item_name}</p>
                                {item.quantity > 1 && (
                                  <Badge variant="outline">x{item.quantity}</Badge>
                                )}
                                {item.category && (
                                  <Badge className="bg-blue-100 text-blue-700">{item.category}</Badge>
                                )}
                                {item.white_goods_cover && (
                                  <Badge className="bg-purple-100 text-purple-700">
                                    <Shield className="w-3 h-3 mr-1" />
                                    Covered
                                  </Badge>
                                )}
                              </div>
                              {item.manufacturer && (
                                <p className="text-sm text-gray-500">{item.manufacturer}</p>
                              )}
                              <div className="flex items-center gap-4 mt-1 text-sm">
                                <Badge className={CONDITION_OPTIONS.find(c => c.value === item.condition)?.color || ''}>
                                  {item.condition}
                                </Badge>
                                {item.purchase_price && (
                                  <span className="text-gray-500">
                                    Purchase: £{item.purchase_price.toLocaleString()}
                                  </span>
                                )}
                                {item.depreciated_value !== null && (
                                  <span className="text-amber-600">
                                    Current: £{item.depreciated_value.toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleRemoveItem(item.id)}
                              className="text-red-500 hover:text-red-700"
                              data-testid={`remove-item-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Add Item Tab */}
          {/* Quick Add Tab */}
          <TabsContent value="quick" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Quick Add Common Items
                </CardTitle>
                <CardDescription>
                  Click any item below to instantly add it to your inventory with typical values. 
                  You can edit details after adding.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Kitchen Appliances Section */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Refrigerator className="w-4 h-4" />
                    Kitchen Appliances (White Goods)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {QUICK_ADD_PRESETS.filter(p => p.category === 'white_goods').map(preset => {
                      const Icon = preset.icon;
                      return (
                        <button
                          key={preset.id}
                          onClick={() => handleQuickAdd(preset)}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${preset.color}`}
                          data-testid={`quick-add-${preset.id}`}
                        >
                          <Icon className="w-6 h-6 mb-2 text-gray-600" />
                          <p className="font-medium text-sm">{preset.name}</p>
                          <p className="text-xs text-gray-500">£{preset.purchase_price}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Furniture Section */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Sofa className="w-4 h-4" />
                    Furniture
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {QUICK_ADD_PRESETS.filter(p => p.category === 'furniture').map(preset => {
                      const Icon = preset.icon;
                      return (
                        <button
                          key={preset.id}
                          onClick={() => handleQuickAdd(preset)}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${preset.color}`}
                          data-testid={`quick-add-${preset.id}`}
                        >
                          <Icon className="w-6 h-6 mb-2 text-gray-600" />
                          <p className="font-medium text-sm">{preset.name}</p>
                          <p className="text-xs text-gray-500">£{preset.purchase_price}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Electronics & Fixtures Section */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Tv className="w-4 h-4" />
                    Electronics & Fixtures
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {QUICK_ADD_PRESETS.filter(p => p.category === 'electronics' || p.category === 'fixtures').map(preset => {
                      const Icon = preset.icon;
                      return (
                        <button
                          key={preset.id}
                          onClick={() => handleQuickAdd(preset)}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${preset.color}`}
                          data-testid={`quick-add-${preset.id}`}
                        >
                          <Icon className="w-6 h-6 mb-2 text-gray-600" />
                          <p className="font-medium text-sm">{preset.name}</p>
                          <p className="text-xs text-gray-500">£{preset.purchase_price}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-800">Quick Add Tips</h4>
                    <ul className="text-sm text-blue-700 mt-1 space-y-1">
                      <li>• Items are added with current year as purchase date</li>
                      <li>• White goods automatically have cover enabled</li>
                      <li>• You can edit any details in the Items tab after adding</li>
                      <li>• Use "Custom Item" tab for items not listed here</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Add Item Tab */}
          <TabsContent value="add" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Add Inventory Item</CardTitle>
                <CardDescription>
                  Add items with category, purchase details, and white goods cover options
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Room and Item Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Room *</Label>
                    <select
                      value={newItem.room}
                      onChange={(e) => setNewItem(prev => ({ ...prev, room: e.target.value }))}
                      className="w-full border rounded-lg p-2"
                      data-testid="add-room-select"
                    >
                      <option value="">Select room...</option>
                      {ROOM_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Item Name *</Label>
                    <Input
                      value={newItem.item_name}
                      onChange={(e) => setNewItem(prev => ({ ...prev, item_name: e.target.value }))}
                      placeholder="e.g. Fridge Freezer, Dining Table"
                      data-testid="add-item-name"
                    />
                  </div>
                </div>
                
                {/* Category and Subcategory */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <select
                      value={newItem.category}
                      onChange={(e) => setNewItem(prev => ({ 
                        ...prev, 
                        category: e.target.value,
                        subcategory: '',
                        white_goods_cover: false
                      }))}
                      className="w-full border rounded-lg p-2"
                      data-testid="add-category-select"
                    >
                      <option value="">Select category...</option>
                      {Object.keys(categories).map(cat => (
                        <option key={cat} value={cat}>
                          {cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Subcategory</Label>
                    <select
                      value={newItem.subcategory}
                      onChange={(e) => setNewItem(prev => ({ ...prev, subcategory: e.target.value }))}
                      className="w-full border rounded-lg p-2"
                      disabled={!newItem.category}
                      data-testid="add-subcategory-select"
                    >
                      <option value="">Select subcategory...</option>
                      {getSubcategories().map(sub => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Purchase Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Year of Purchase</Label>
                    <Input
                      type="number"
                      min="1990"
                      max={new Date().getFullYear()}
                      value={newItem.year_of_purchase}
                      onChange={(e) => setNewItem(prev => ({ ...prev, year_of_purchase: e.target.value }))}
                      data-testid="add-year"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Purchase Price (£)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={newItem.purchase_price}
                      onChange={(e) => setNewItem(prev => ({ ...prev, purchase_price: e.target.value }))}
                      placeholder="0.00"
                      data-testid="add-price"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Manufacturer/Brand</Label>
                    <Input
                      value={newItem.manufacturer}
                      onChange={(e) => setNewItem(prev => ({ ...prev, manufacturer: e.target.value }))}
                      placeholder="e.g. Bosch, IKEA"
                      data-testid="add-manufacturer"
                    />
                  </div>
                </div>
                
                {/* Depreciation Preview */}
                {newItem.purchase_price && newItem.year_of_purchase && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Calculator className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-800">Depreciation Preview</h4>
                        {(() => {
                          const calc = calculateDepreciation(
                            parseFloat(newItem.purchase_price),
                            parseInt(newItem.year_of_purchase)
                          );
                          if (!calc) return null;
                          return (
                            <div className="text-sm text-blue-700 mt-1">
                              <p>Age: {calc.ageYears} years ({calc.depreciationPercent}% depreciated)</p>
                              <p className="font-semibold">
                                Current Value: £{calc.depreciatedValue.toFixed(2)}
                              </p>
                              {calc.whiteGoodsCoverEligible ? (
                                <p className="text-green-600 mt-1">✓ Eligible for white goods cover</p>
                              ) : (
                                <p className="text-amber-600 mt-1">✗ Not eligible for white goods cover (over 3 years old)</p>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* White Goods Cover Toggle */}
                {newItem.category === 'white_goods' && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-purple-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-purple-800">White Goods Cover</h4>
                          <p className="text-sm text-purple-600">
                            Enable insurance cover for breakdown and repairs
                          </p>
                          {(() => {
                            const calc = calculateDepreciation(
                              parseFloat(newItem.purchase_price) || 0,
                              parseInt(newItem.year_of_purchase)
                            );
                            if (calc && !calc.whiteGoodsCoverEligible) {
                              return (
                                <p className="text-sm text-amber-600 mt-1">
                                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                                  Item is over 3 years old - not eligible
                                </p>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                      <Switch
                        checked={newItem.white_goods_cover}
                        onCheckedChange={(checked) => setNewItem(prev => ({ 
                          ...prev, 
                          white_goods_cover: checked 
                        }))}
                        disabled={(() => {
                          const calc = calculateDepreciation(
                            parseFloat(newItem.purchase_price) || 0,
                            parseInt(newItem.year_of_purchase)
                          );
                          return calc && !calc.whiteGoodsCoverEligible;
                        })()}
                        data-testid="white-goods-cover-toggle"
                      />
                    </div>
                  </div>
                )}
                
                {/* Condition and Quantity */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Condition</Label>
                    <select
                      value={newItem.condition}
                      onChange={(e) => setNewItem(prev => ({ ...prev, condition: e.target.value }))}
                      className="w-full border rounded-lg p-2"
                      data-testid="add-condition"
                    >
                      {CONDITION_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      data-testid="add-quantity"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={newItem.description}
                      onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Optional description"
                      data-testid="add-description"
                    />
                  </div>
                </div>
                
                {/* Notes */}
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <textarea
                    value={newItem.notes}
                    onChange={(e) => setNewItem(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional notes about the item"
                    className="w-full border rounded-lg p-2 h-20"
                    data-testid="add-notes"
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  onClick={handleAddItem}
                  disabled={!newItem.room || !newItem.item_name}
                  className="w-full"
                  data-testid="add-item-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item to Inventory
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          {/* Declaration Tab */}
          <TabsContent value="declaration" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSignature className="w-5 h-5" />
                  Landlord Declaration
                </CardTitle>
                <CardDescription>
                  Review and accept the declaration before submitting the inventory
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-gray-50 border rounded-lg p-6">
                  <h4 className="font-semibold mb-4">Declaration Statement</h4>
                  <p className="text-gray-700 leading-relaxed">
                    I hereby confirm that the inventory listed above is a true and accurate representation 
                    of all fixtures, fittings, and contents provided with this property. I understand that 
                    this inventory will be used as the baseline for the tenant's check-in inspection and 
                    any subsequent check-out inspection. I acknowledge that any items not listed may not 
                    be covered in deposit deduction claims.
                  </p>
                  <div className="mt-4 space-y-2 text-gray-700">
                    <p><strong>By signing this declaration, I confirm:</strong></p>
                    <ol className="list-decimal list-inside space-y-1 text-sm">
                      <li>All items listed are present at the property</li>
                      <li>The condition descriptions are accurate to the best of my knowledge</li>
                      <li>I have disclosed all known defects or damage</li>
                      <li>This inventory may be used as evidence in deposit disputes</li>
                    </ol>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 border rounded-lg">
                  <Switch
                    checked={declarationAccepted}
                    onCheckedChange={setDeclarationAccepted}
                    data-testid="accept-declaration"
                  />
                  <Label className="cursor-pointer">
                    I have read and accept the above declaration
                  </Label>
                </div>
                
                {items.length === 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                    <Info className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">No Items Added</h4>
                      <p className="text-sm text-amber-700">
                        Please add items to the inventory before submitting
                      </p>
                    </div>
                  </div>
                )}
                
                {!selectedProperty && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                    <Info className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">No Property Selected</h4>
                      <p className="text-sm text-amber-700">
                        Please select a property at the top of the page
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
              <CardFooter>
                <Button 
                  onClick={handleSaveInventory}
                  disabled={saving || items.length === 0 || !selectedProperty || !declarationAccepted}
                  className="w-full"
                  data-testid="submit-inventory-btn"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating Inventory...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" />Create & Sign Inventory</>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
